import type { Theme } from '@mui/material/styles';

/** Quanto dura l'evidenza della voce raggiunta dal link di una notifica (#70). */
export const THREAD_HIGHLIGHT_MS = 2_500;

/** Attributo che evidenzia la voce raggiunta: lo stile è in `TicketThread`. */
export const THREAD_HIGHLIGHT_ATTRIBUTE = 'data-highlighted';

/** Attributo che marca le voci del thread: solo loro si raggiungono con l'ancora. */
export const THREAD_ENTRY_ATTRIBUTE = 'data-thread-entry';

/**
 * Stile comune delle voci raggiungibili dal link di una notifica (#70), messaggi ed eventi:
 * evidenza breve, senza transizione con «riduci movimento»; il margine la lascia staccata dal bordo
 * quando scorre in vista.
 */
export const THREAD_TARGET_SX = {
  scrollMarginTop: 16,
  transition: 'box-shadow 300ms ease-out',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  [`&[${THREAD_HIGHLIGHT_ATTRIBUTE}]`]: { boxShadow: (theme: Theme) => `0 0 0 3px ${theme.palette.primary.main}` },
} as const;

/** L'ancora del frammento dell'URL (`#m-<id>` → `m-<id>`); vuota se assente o illeggibile. */
export function anchorFromHash(hash: string): string {
  try {
    return decodeURIComponent(hash.replace(/^#/, ''));
  } catch {
    return '';
  }
}

/** Il primo antenato che scorre in verticale: il pannello del dettaglio (#106), o la pagina. */
function scrollContainer(element: HTMLElement): HTMLElement {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return document.documentElement;
}

/** Gesti con cui chi legge prende in mano lo scorrimento: da lì in poi la voce non si riallinea più. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * Porta alla voce del thread con l'ancora indicata (#70): la fa scorrere in vista (immediato con
 * `prefers-reduced-motion`), le dà il focus e la evidenzia per `THREAD_HIGHLIGHT_MS`. Agisce solo
 * su una voce del thread (`THREAD_ENTRY_ATTRIBUTE`), mai su un altro elemento con lo stesso `id`.
 *
 * I blocchi sopra il thread possono crescere dopo lo scorrimento (lo stato degli allegati arriva
 * dopo il dettaglio): finché dura l'evidenza, e finché chi legge non scorre da sé, la voce si
 * riallinea a ogni cambio di altezza del contenuto (#120, visto nel browser).
 * @returns la pulizia dell'evidenza, oppure `null` se la voce non c'è (il chiamante fa come prima)
 */
export function revealThreadEntry(anchor: string): (() => void) | null {
  const entry = anchor ? document.getElementById(anchor) : null;
  if (!entry?.hasAttribute(THREAD_ENTRY_ATTRIBUTE)) return null;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const scroll = () => entry.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  scroll();
  // Lo scorrimento lo fa `scrollIntoView`: il focus non deve saltare di colpo alla voce.
  entry.focus({ preventScroll: true });
  // Attributo fuori dal controllo di React: lo toglie il timer, o la pulizia se il dettaglio si chiude prima.
  entry.setAttribute(THREAD_HIGHLIGHT_ATTRIBUTE, '');

  const container = scrollContainer(entry);
  // Posizione della voce nel contenuto che scorre: cambia solo se cambia l'impaginazione sopra.
  const offset = () =>
    entry.getBoundingClientRect().top -
    (container === document.documentElement ? 0 : container.getBoundingClientRect().top) +
    container.scrollTop;
  let lastTop = offset();
  const realign = () => {
    const top = offset();
    if (top !== lastTop) scroll();
    lastTop = top;
  };
  const resizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(realign);
  for (const child of Array.from(container.children)) resizes?.observe(child);
  const stopFollowing = () => {
    resizes?.disconnect();
    for (const type of USER_SCROLL_EVENTS) container.removeEventListener(type, stopFollowing);
  };
  for (const type of USER_SCROLL_EVENTS) container.addEventListener(type, stopFollowing, { passive: true });

  const timer = window.setTimeout(() => {
    entry.removeAttribute(THREAD_HIGHLIGHT_ATTRIBUTE);
    stopFollowing();
  }, THREAD_HIGHLIGHT_MS);
  return () => {
    window.clearTimeout(timer);
    stopFollowing();
    entry.removeAttribute(THREAD_HIGHLIGHT_ATTRIBUTE);
  };
}
